import React, { useMemo } from 'react';
import { costFormater, MaxElectionAmountType } from '@common-fe/common-fe';

import type { Plan } from '@/modules/plan/plan.types';

export interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

interface FAQDataParams {
  partnerName?: string;
  rolloverPlans?: Plan[];
}

export const useRenewalFAQData = (params?: FAQDataParams): FAQItem[] => {
  const faqData = useMemo(() => {
    const partnerName = params?.partnerName || '[Partner]';
    const rolloverPlans = params?.rolloverPlans || [];
    const buildRolloverText = (plan: Plan, index: number) => {
      const maxRolloverAmount = plan.maxRolloverAmount;
      const maxRolloverAmountType = plan.maxRolloverAmountType;
      const minRolloverAmount = plan.minRolloverAmount;
      const daysAfterPlanEnd = plan.daysAfterPlanEnd;
      const rolloverClaimsDays = plan.rolloverClaimsDaysAmount;
      const isAutoEnrollment = plan.isAutoEnrollment;
      const accountType = plan.type;

      return (
        <React.Fragment key={plan.id}>
          <strong style={{ fontWeight: 700 }}>
            {index + 1}. {plan.name}:
          </strong>
          {'\n'}

          {!daysAfterPlanEnd ? (
            <>
              At the end of the plan year, unused funds will roll into the participant's new plan.
              {'\n\n'}
            </>
          ) : daysAfterPlanEnd > 0 ? (
            <>
              <strong style={{ fontWeight: 700 }}>{daysAfterPlanEnd} days</strong> after the plan year end
              date, unused funds will roll into the participant's new plan.
              {'\n\n'}
            </>
          ) : null}

          {minRolloverAmount ? (
            <>
              Rollovers require a minimum of{' '}
              <strong style={{ fontWeight: 700 }}>{costFormater(minRolloverAmount, true)}</strong> to qualify
              for roll over to the new account.
              {'\n\n'}
            </>
          ) : null}

          {maxRolloverAmount && maxRolloverAmountType !== MaxElectionAmountType.UNLIMITED ? (
            <>
              There is a maximum limit of{' '}
              <strong style={{ fontWeight: 700 }}>{costFormater(maxRolloverAmount, true)}</strong> for
              rollovers.
              {'\n\n'}
            </>
          ) : null}

          {rolloverClaimsDays ? (
            <>
              Rolled over funds can be used for current-year expenses and for prior-year dates of service only
              within the first <strong style={{ fontWeight: 700 }}>{rolloverClaimsDays}</strong> days of the
              new plan year.
              {'\n\n'}
            </>
          ) : null}

          {isAutoEnrollment && accountType && (
            <>
              {isAutoEnrollment ? (
                <>
                  Employees not enrolled in an <strong style={{ fontWeight: 700 }}>{accountType}</strong> for
                  the new plan year who have unused funds that qualify to roll over will be automatically
                  enrolled.
                  {'\n\n'}
                </>
              ) : (
                <>
                  Employees not already enrolled in an{' '}
                  <strong style={{ fontWeight: 700 }}>{accountType}</strong> for the new plan year will not
                  qualify for rollover.
                  {'\n\n'}
                </>
              )}
            </>
          )}
        </React.Fragment>
      );
    };

    const rolloverAnswer = <>{rolloverPlans.map((plan, index) => buildRolloverText(plan, index))}</>;

    const faqItems = [
      {
        question: 'How does renew process work?',
        answer: (
          <>
            <strong style={{ fontWeight: 700 }}>
              Your organization's plan year is ending. Set up next year's plan in a few simple steps. The
              system will guide you through the process.
            </strong>
            {'\n\n'}
            1. Select whether to keep your current plans as they are or request changes.
            {'\n'}
            2. If changes are requested, a representative from your partner will contact you to confirm the
            details. This completes the renewal process.
            {'\n'}
            3. If no changes are needed, the system will display a summary for your review and approval.
            {'\n'}
            4. After you confirm, your selections will be securely saved and automatically applied on the
            renewal date.
          </>
        ),
      },
      {
        question: 'What will happen to my organization if the plans are not renewed?',
        answer: (
          <>
            Your plans will run according to their current design.
            {'\n'}
            Contact <strong style={{ fontWeight: 700 }}>{partnerName}</strong> to review options.
          </>
        ),
      },
    ];

    if (rolloverPlans.length > 0) {
      faqItems.push({
        question: 'How does a rollover work?',
        answer: rolloverAnswer,
      });
    }

    return faqItems;
  }, [params]);

  return faqData;
};
